import React, { useState, useRef, useEffect } from 'react';
import { useCountry } from '../context/CountryContext';
import { COUNTRIES } from '../constants/countries';
import { ChevronDown } from 'lucide-react';

export function CountrySelector({ compact = false, iconOnly = false, align = 'right' }: { compact?: boolean, iconOnly?: boolean, align?: 'left' | 'right' }) {
  const { activeCountry, setActiveCountry } = useCountry();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-slate-600 hover:text-[#FF6600] font-bold transition-colors gap-1.5 cursor-pointer"
      >
        <img 
          src={`https://flagcdn.com/w40/${activeCountry.code.toLowerCase()}.png`} 
          width={iconOnly ? "28" : "20"}
          alt={activeCountry.name}
          className={`${iconOnly ? 'w-7' : 'w-4'} h-auto rounded-sm shadow-sm`}
        />
        {!iconOnly && (
          <span className={compact ? "text-[11px] uppercase tracking-wider" : "uppercase tracking-wider"}>
            {compact ? activeCountry.name : `${activeCountry.name} (${activeCountry.currency})`}
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-2 w-auto min-w-[160px] whitespace-nowrap bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[2100] animate-in fade-in slide-in-from-top-2 duration-150`}>
          {COUNTRIES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                setActiveCountry(country.code);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-xs font-bold transition-colors flex items-center gap-2 ${
                activeCountry.code === country.code
                  ? 'bg-orange-50 text-orange-600'
                  : 'text-slate-600 hover:bg-gray-50 hover:text-[#FF6600]'
              }`}
            >
              <div className="flex items-center gap-2">
                <img 
                  src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`} 
                  srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                  width="20"
                  alt={country.name}
                  className="w-4 h-auto rounded-sm shadow-sm"
                />
                <span>{country.name}</span>
              </div>
              <span className="text-[10px] opacity-75 font-semibold">({country.currency})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
